// Custom indexed-bitmap format used inside .munos script literals.
//
// Why this exists: a sprite at this scale (8x8 to 32x32) with at most
// a handful of palette indices is wasteful to ship as PNG. We pack the
// pixels at a per-bitmap bit depth — slot 0 transparent, slots 1..N
// opaque — behind a 3-byte (width, height, bpp) header. For a 24x24
// NES sprite at 2bpp this is ~150 bytes vs ~300-500 for the same
// picture in PNG; the .munos file already ships its assets as base64
// literals inline, so smaller bytes mean shorter scripts.
//
// Why per-bitmap bpp (rather than a single global bit depth): the
// supported emulator systems sit at very different palette sizes.
// A solo NES sprite needs only 3 opaque colors (2bpp suffices),
// double-stacked NES clusters need up to 6 (3bpp), an SNES sprite
// uses 16 (4bpp), and SNES Mode 7 / 256-color tilemaps need 8bpp.
// A global bit depth would either bloat small NES sprites with
// unused headroom, or cap out below what SNES needs. Storing the
// bpp in each bitmap's header lets every asset pay for exactly the
// bits it uses while keeping the decoder console-agnostic — index N
// is index N regardless of how it was packed, and palette lookup
// at draw_image time is unchanged.
//
// Format (everything here is single bytes or packed bits — no multi-
// byte ints):
//
//   offset | size | field
//   -------|------|------
//   0      | 1    | width   (u8, 1..255)
//   1      | 1    | height  (u8, 1..255)
//   2      | 1    | bpp     (u8, 1..8)
//   3..    | N    | packed indexed pixels, `bpp` bits per pixel,
//                   MSB-first bitstream, row-major, length
//                   ceil(w*h*bpp/8)
//
// The bitstream is MSB-first: pixel i occupies bit positions
// (i*bpp .. i*bpp+bpp-1) counting from the most significant bit of
// byte (3 + (i*bpp / 8)). A pixel may straddle a byte boundary —
// the high bits go into the current byte's low positions, the low
// bits into the next byte's high positions. Standard PNG/JPEG-style
// packing. At bpp=8 the packer degenerates to one byte per pixel
// with no shifting; at bpp=1 a byte holds eight pixels.
//
// Earlier versions of this format carried two i8 origin offsets at
// bytes 2-3. They were retired in favor of script-author-supplied
// animation-alignment offsets at the call site
// (`image_from_bitmap_bytes(bytes, dx, dy)`) — see the .munos
// docstring for that builtin.
// Pick the smallest bpp (1..8) that can represent every value in
// `pixels`. Used by encodeBitmap when the caller doesn't specify a
// depth — keeps the on-disk size minimal without making the caller
// think about it.
export function pickBpp(pixels) {
    let max = 0;
    for (let i = 0; i < pixels.length; i++) {
        const v = pixels[i];
        if (v > max)
            max = v;
    }
    if (max > 255)
        throw new RangeError(`bitmap pixel value ${max} exceeds u8 range`);
    // ceil(log2(max + 1)), clamped to [1, 8]. max=0 → 1bpp (a single
    // all-transparent bitmap still needs at least one bit per pixel).
    let bpp = 1;
    while ((1 << bpp) <= max)
        bpp++;
    return bpp;
}
// Encode indexed pixels into the bitmap byte format. If `bpp` is
// omitted, picks the smallest depth that fits every value. Throws on
// out-of-range w/h, bpp outside 1..8, or pixel values that don't fit
// in the chosen bpp.
export function encodeBitmap(width, height, pixels, bpp) {
    if (width < 1 || width > 255)
        throw new RangeError(`bitmap width out of u8 range: ${width}`);
    if (height < 1 || height > 255)
        throw new RangeError(`bitmap height out of u8 range: ${height}`);
    if (pixels.length !== width * height) {
        throw new RangeError(`bitmap pixels length ${pixels.length} != w*h ${width * height}`);
    }
    const depth = bpp ?? pickBpp(pixels);
    if (depth < 1 || depth > 8)
        throw new RangeError(`bitmap bpp out of range 1..8: ${depth}`);
    const maxVal = depth === 8 ? 0xff : (1 << depth) - 1;
    const totalBits = pixels.length * depth;
    const packedLen = (totalBits + 7) >> 3;
    const out = new Uint8Array(3 + packedLen);
    out[0] = width;
    out[1] = height;
    out[2] = depth;
    for (let i = 0; i < pixels.length; i++) {
        const v = pixels[i];
        if (v > maxVal)
            throw new RangeError(`bitmap pixel ${i} value ${v} out of 0..${maxVal} range for ${depth}bpp`);
        const bitPos = i * depth;
        const byteIdx = 3 + (bitPos >> 3);
        const bitInByte = bitPos & 7;
        const space = 8 - bitInByte; // bits available in current byte (1..8)
        if (space >= depth) {
            // Whole pixel fits in the current byte. Place at MSB-aligned
            // offset (space - depth) so pixel 0 of a byte sits at bits
            // 7..(7-depth+1).
            out[byteIdx] |= (v << (space - depth)) & 0xff;
        }
        else {
            // Straddle: write the top `space` bits of v into the current
            // byte's low positions, the remaining (depth - space) bits into
            // the next byte's MSB positions.
            const hiBits = depth - space; // bits going into next byte (1..7)
            const hi = (v >> hiBits) & ((1 << space) - 1);
            const lo = v & ((1 << hiBits) - 1);
            out[byteIdx] |= hi;
            out[byteIdx + 1] |= (lo << (8 - hiBits)) & 0xff;
        }
    }
    return out;
}
// Decode the bitmap byte format. Returns null on malformed input
// (length too short for the declared dimensions, bpp out of range,
// etc).
export function decodeBitmap(bytes) {
    if (bytes.length < 3)
        return null;
    const width = bytes[0];
    const height = bytes[1];
    const bpp = bytes[2];
    if (width === 0 || height === 0)
        return null;
    if (bpp < 1 || bpp > 8)
        return null;
    const totalBits = width * height * bpp;
    const packedLen = (totalBits + 7) >> 3;
    if (bytes.length < 3 + packedLen)
        return null;
    const mask = bpp === 8 ? 0xff : (1 << bpp) - 1;
    const pixels = new Uint8Array(width * height);
    for (let i = 0; i < pixels.length; i++) {
        const bitPos = i * bpp;
        const byteIdx = 3 + (bitPos >> 3);
        const bitInByte = bitPos & 7;
        const space = 8 - bitInByte;
        if (space >= bpp) {
            pixels[i] = (bytes[byteIdx] >> (space - bpp)) & mask;
        }
        else {
            const hiBits = bpp - space;
            const hi = bytes[byteIdx] & ((1 << space) - 1);
            const lo = (bytes[byteIdx + 1] >> (8 - hiBits)) & ((1 << hiBits) - 1);
            pixels[i] = (hi << hiBits) | lo;
        }
    }
    return { width, height, bpp, pixels };
}
//# sourceMappingURL=bitmap.js.map